import React, { useEffect, useRef, useState, useCallback } from "react";

import StartScreen from "./components/StartScreen";
import CandidateForm from "./components/CandidateForm";
import PermissionModal from "./components/PermissionModal";
import QuestionScreen from "./components/QuestionScreen";
import TypingTestScreen from "./components/TypingTestScreen";
import ResultScreen from "./components/ResultScreen";
import ViolationWarningModal from "./components/ViolationWarningModal";
import AdminPanel from "./components/AdminPanel";
import QuizHeader from "./components/quiz/QuizHeader";

import { pharmacyQuestions as defaultQuestions } from "./questions/pharmacy";
import { translations } from "./translations";
import { fetchQuestions, submitResult } from "./services/api";
import { resolveTypingPassages } from "./utils/typingTest";
import { pickRandomQuestions } from "./utils/quiz";
import {
  clearAdminAuth,
  isAdminModeActive,
  setAdminMode,
} from "./utils/adminSession";

const MAX_ATTEMPTS = 2;
const LOCAL_QUESTIONS_KEY = "hevmed_questions_fallback";
const LOCAL_CONFIG_KEY = "hevmed_exam_config_fallback";
const LOCAL_TYPING_PASSAGES_KEY = "hevmed_typing_passages_fallback";

const requestFullscreen = () => {
  const el = document.documentElement;
  if (el.requestFullscreen) el.requestFullscreen();
};

const safeExitFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => { });
  }
};

function App() {
  const [step, setStep] = useState("start");
  const lang = "en";
  const [user, setUser] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionLimit, setQuestionLimit] = useState(10);
  const [typingPassages, setTypingPassages] = useState([]);
  const [examQuestions, setExamQuestions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(isAdminModeActive);

  const [showPermission, setShowPermission] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);

  const [terminated, setTerminated] = useState(false);
  const [terminationReason, setTerminationReason] = useState("");
  const terminatedRef = useRef(false);

  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [showViolation, setShowViolation] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [appError, setAppError] = useState("");
  const [timeTakenSeconds, setTimeTakenSeconds] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [typingTestResult, setTypingTestResult] = useState(null);

  const examStartTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const quizTimeTakenRef = useRef(0);
  const stepRef = useRef(step);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    let isMounted = true;

    const loadQuestions = async () => {
      setIsLoadingQuestions(true);
      setAppError("");

      try {
        const data = await fetchQuestions();
        if (isMounted) {
          setQuestions(data.questions);
          setQuestionLimit(data.questionLimit);
          setTypingPassages(resolveTypingPassages(data.typingPassages));
        }
      } catch (error) {
        if (isMounted) {
          const savedLocalQuestions = localStorage.getItem(LOCAL_QUESTIONS_KEY);
          setQuestions(savedLocalQuestions ? JSON.parse(savedLocalQuestions) : defaultQuestions);
          const savedLimit = localStorage.getItem(LOCAL_CONFIG_KEY);
          setQuestionLimit(savedLimit ? Number(savedLimit) : 10);
          const savedPassages = localStorage.getItem(LOCAL_TYPING_PASSAGES_KEY);
          setTypingPassages(resolveTypingPassages(savedPassages ? JSON.parse(savedPassages) : []));
          setAppError("Cloud database is not reachable. Falling back to local/default questions.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingQuestions(false);
        }
      }
    };

    loadQuestions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (step !== "exam" && step !== "typing-test") {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return undefined;
    }

    if (step === "typing-test") {
      return undefined;
    }

    timerIntervalRef.current = setInterval(() => {
      if (examStartTimeRef.current) {
        setElapsedSeconds(Math.floor((Date.now() - examStartTimeRef.current) / 1000));
      }
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [step]);

  const freezeQuizTime = useCallback(() => {
    const taken = examStartTimeRef.current
      ? Math.round((Date.now() - examStartTimeRef.current) / 1000)
      : 0;
    quizTimeTakenRef.current = taken;
    setTimeTakenSeconds(taken);
    examStartTimeRef.current = null;
  }, []);

  const goToTypingTest = useCallback(() => {
    freezeQuizTime();
    setTypingTestResult(null);
    setStep("typing-test");
  }, [freezeQuizTime]);

  const terminateExam = useCallback((reason) => {
    if (terminatedRef.current) return;

    setTerminated(true);
    setTerminationReason(reason);
    goToTypingTest();
  }, [goToTypingTest]);

  const handleViolation = useCallback((reason) => {
    if (terminatedRef.current || stepRef.current === "typing-test") return;

    setAttemptsUsed((prev) => {
      const next = prev + 1;
      if (next > MAX_ATTEMPTS) {
        terminateExam(reason);
        return next;
      }
      setTerminationReason(reason);
      setShowViolation(true);
      return next;
    });
  }, [terminateExam]);

  useEffect(() => {
    if (step !== "exam" && step !== "typing-test") return;

    const onVisibility = () => {
      if (document.hidden) handleViolation("Tab switching detected");
    };

    const onFullscreen = () => {
      if (!document.fullscreenElement) handleViolation("Exited fullscreen mode");
    };

    const onBeforeUnload = (e) => {
      if (terminatedRef.current) return;
      handleViolation("Attempted to refresh or close the browser");
      e.preventDefault();
      e.returnValue = "";
    };

    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("fullscreenchange", onFullscreen);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("fullscreenchange", onFullscreen);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [step, handleViolation]);

  const handleStartDescription = () => {
    setStep("form");
  };

  const handleFormSubmit = (userData) => {
    setUser(userData);
    setShowPermission(true);
  };

  const acceptPermission = () => {
    const selected = pickRandomQuestions(questions, questionLimit);
    setExamQuestions(selected);
    setShowPermission(false);
    setStep("exam");
    requestFullscreen();
    setAnswers(Array(selected.length).fill(""));
    setCurrent(0);
    setTerminated(false);
    setTerminationReason("");
    setAttemptsUsed(0);
    setShowViolation(false);
    setTimeTakenSeconds(0);
    setElapsedSeconds(0);
    setTypingTestResult(null);
    quizTimeTakenRef.current = 0;
    terminatedRef.current = false;
    examStartTimeRef.current = Date.now();
  };

  const selectOptionOrText = (val) => {
    const updated = [...answers];
    updated[current] = val;
    setAnswers(updated);
  };

  const nextQuestion = () => {
    if (current < examQuestions.length - 1) {
      setCurrent(current + 1);
    } else {
      goToTypingTest();
    }
  };

  const finishExam = async (typingMetrics) => {
    terminatedRef.current = true;
    setStep("result");
    safeExitFullscreen();

    if (typingMetrics) {
      setTypingTestResult(typingMetrics);
    }

    const score = answers.reduce((acc, ans, i) => {
      if (examQuestions[i].type === "text") return acc;
      return ans === examQuestions[i][lang].answer ? acc + 1 : acc;
    }, 0);

    const mcqTotal = examQuestions.filter((q) => q.type !== "text").length;

    const result = {
      candidate: user,
      score,
      total: mcqTotal,
      timestamp: new Date().toISOString(),
      answers,
      questionIds: examQuestions.map((q) => String(q._id || "")),
      timeTakenSeconds: quizTimeTakenRef.current,
      lang,
      typingTest: typingMetrics || null,
    };

    try {
      await submitResult(result);
    } catch (error) {
      setAppError(error.message || "Failed to save result.");
    }
  };

  const handleTypingComplete = (metrics) => {
    finishExam(metrics);
  };

  if (isAdmin) {
    return (
      <div className="app-shell admin-app-shell">
        <AdminPanel
            onExit={() => {
              clearAdminAuth();
              setIsAdmin(false);
            }}
            lang={lang}
            currentQuestions={questions}
          onUpdateQuestions={(newQs, limit, passages) => {
            setQuestions(newQs);
            if (limit) setQuestionLimit(limit);
            if (passages) setTypingPassages(passages);
          }}
        />
      </div>
    );
  }

  const currentQuestion = examQuestions[current]
    ? {
      section: examQuestions[current].section,
      type: examQuestions[current].type || "mcq",
      content: examQuestions[current][lang],
    }
    : null;

  const score =
    step === "result" && !terminated
      ? answers.reduce((acc, ans, i) => {
        if (examQuestions[i]?.type === "text") return acc;
        return ans === examQuestions[i][lang].answer ? acc + 1 : acc;
      }, 0)
      : 0;

  const mcqTotal = examQuestions.filter((q) => q.type !== "text").length;

  return (
    <div className="app-shell">
      <div className="bg-blob bg-blob-1" aria-hidden="true" />
      <div className="bg-blob bg-blob-2" aria-hidden="true" />
      <div className="bg-blob bg-blob-3" aria-hidden="true" />

      <div className={`container ${step}-step step-enter`}>

      {appError && (
        <div className="app-error-banner">
          {appError}
        </div>
      )}

      {isLoadingQuestions ? (
        <div className="page-wrap">
          <div className="card">
            <h2>{translations[lang].title}</h2>
            <div className="skeleton-container">
              <div className="skeleton-card">
                <div className="skeleton-line title" />
                <div className="skeleton-line text" />
                <div className="skeleton-line text short" />
              </div>
            </div>
            <p className="loading-text">{translations[lang].loadingQuestions}</p>
          </div>
        </div>
      ) : (
        <>
          {step === "start" && <StartScreen lang={lang} questionLimit={questionLimit} onStart={handleStartDescription} />}

          {step === "form" && !showPermission && (
            <CandidateForm lang={lang} onSubmit={handleFormSubmit} />
          )}

          {showPermission && (
            <PermissionModal
              onAccept={acceptPermission}
              onDecline={() => setShowPermission(false)}
              lang={lang}
            />
          )}

          {showViolation && (
            <ViolationWarningModal
              attemptsLeft={MAX_ATTEMPTS - attemptsUsed + 1}
              onContinue={() => {
                setShowViolation(false);
                requestFullscreen();
              }}
              lang={lang}
            />
          )}

          {step === "exam" && currentQuestion && !showViolation && (
            <>
              <QuizHeader
                current={current}
                total={examQuestions.length}
                elapsedSeconds={elapsedSeconds}
                lang={lang}
              />
              <QuestionScreen
                key={current}
                question={currentQuestion}
                index={current}
                total={examQuestions.length}
                selected={answers[current]}
                onSelect={selectOptionOrText}
                onNext={nextQuestion}
                lang={lang}
              />
            </>
          )}

          {step === "typing-test" && (
            <TypingTestScreen
              typingPassages={typingPassages}
              onComplete={handleTypingComplete}
            />
          )}

          {step === "result" && (
            <ResultScreen
              score={score}
              total={mcqTotal}
              user={user}
              terminated={terminated}
              reason={terminationReason}
              timeTakenSeconds={timeTakenSeconds}
              typingTest={typingTestResult}
              lang={lang}
            />
          )}
        </>
      )}

      <div className="admin-fab">
        <button
          type="button"
          onClick={() => {
            setAdminMode(true);
            setIsAdmin(true);
          }}
          className="admin-fab-btn"
        >
          Admin
        </button>
      </div>
      </div>
    </div>
  );
}

export default App;
